import type { ScanAudioAttachScope } from './scanAudioScopeLib';

export const SPEECH_UNSUPPORTED_MESSAGE = 'Speech to text is not supported in this browser.';

export const VOICE_SCOPE_IGNORED_MESSAGE = 'Ignored: the Kai Agent only responds to task scoping instructions';

export const VOICE_SCOPE_REJECT_GENERIC_MESSAGE = "Didn't respond to this instruction — couldn't apply the change";

/** User-facing chat copy when orchestrator rejects due to low JEV confidence. */
export const buildVoiceScopeLowConfidenceRejectMessage = (confidence: number): string => {
  const percent = Math.round(confidence * 100);
  return `Didn't respond to this instruction — confidence was too low (${percent}%)`;
};

export const buildVoiceScopeRejectMessage = (reject: { confidence?: number }): string =>
  reject.confidence != null
    ? buildVoiceScopeLowConfidenceRejectMessage(reject.confidence)
    : VOICE_SCOPE_REJECT_GENERIC_MESSAGE;

export type VideoToolboxPhotoCaptureLogEntry = {
  kind: 'photo';
  id: string;
  photoId: string;
  capturedAtMs: number;
  message: string;
};

export type VideoToolboxSpeechLogEntry = {
  kind: 'speech';
  id: string;
  updatedAtMs: number;
  text: string;
};

export type VideoToolboxSpeechUnsupportedLogEntry = {
  kind: 'speech-unsupported';
  id: 'speech-unsupported';
  capturedAtMs: number;
  message: string;
};

export type VideoToolboxVoiceAppliedLogEntry = {
  kind: 'voice-applied';
  id: string;
  capturedAtMs: number;
  summary: string;
  utterance: string;
  confidence: number;
  jevDetail: string;
};

export type VideoToolboxVoiceProposedLogEntry = {
  kind: 'voice-proposed';
  id: string;
  proposalId: string;
  capturedAtMs: number;
  summary: string;
  utterance: string;
  confidence: number;
  jevDetail: string;
};

export type VideoToolboxVoiceRejectedLogEntry = {
  kind: 'voice-rejected';
  id: string;
  capturedAtMs: number;
  utterance: string;
  reason: string;
};

export type VideoToolboxActivityLogEntry =
  | VideoToolboxPhotoCaptureLogEntry
  | VideoToolboxSpeechLogEntry
  | VideoToolboxSpeechUnsupportedLogEntry
  | VideoToolboxVoiceAppliedLogEntry
  | VideoToolboxVoiceProposedLogEntry
  | VideoToolboxVoiceRejectedLogEntry;

export type VideoToolboxScopeDisplayNames = {
  groupName?: string;
  taskName?: string;
};

const PHOTO_ADDED_TO_SCOPE_PREFIX = 'Photo added to ';

export const PHOTO_ADDED_TO_SCOPE_MESSAGE_MAX_LENGTH = 60;

/** Truncates v2g-shot photo lines with ellipses only when the full message is 60+ characters. */
export const truncatePhotoAddedToScopeMessage = (message: string): string =>
  message.length >= PHOTO_ADDED_TO_SCOPE_MESSAGE_MAX_LENGTH
    ? `${message.slice(0, PHOTO_ADDED_TO_SCOPE_MESSAGE_MAX_LENGTH - 3)}...`
    : message;

export const buildPhotoAddedToScopeMessage = (scopeDisplayName: string): string =>
  truncatePhotoAddedToScopeMessage(`${PHOTO_ADDED_TO_SCOPE_PREFIX}${scopeDisplayName}`);

export const resolveCaptureDestinationDisplayName = (
  attachScope: ScanAudioAttachScope,
  scopeDisplayNames?: VideoToolboxScopeDisplayNames,
): string => {
  if (attachScope.taskId) {
    return scopeDisplayNames?.taskName?.trim()
      || scopeDisplayNames?.groupName?.trim()
      || 'task';
  }
  return scopeDisplayNames?.groupName?.trim() || 'room';
};

export const buildPhotoCaptureLogEntry = (
  photoId: string,
  scopeDisplayName: string,
  capturedAtMs = Date.now(),
): VideoToolboxPhotoCaptureLogEntry => ({
  kind: 'photo',
  id: photoId,
  photoId,
  capturedAtMs,
  message: buildPhotoAddedToScopeMessage(scopeDisplayName),
});

export const buildSpeechUnsupportedLogEntry = (
  capturedAtMs = Date.now(),
): VideoToolboxSpeechUnsupportedLogEntry => ({
  kind: 'speech-unsupported',
  id: 'speech-unsupported',
  capturedAtMs,
  message: SPEECH_UNSUPPORTED_MESSAGE,
});

export const buildSpeechLogEntry = (
  text: string,
  entryId: string,
  updatedAtMs = Date.now(),
): VideoToolboxSpeechLogEntry => ({
  kind: 'speech',
  id: entryId,
  updatedAtMs,
  text,
});

export type SpeechActivityUpdate = {
  utterance?: string;
  interim?: string;
};

export const getActivityLogEntrySortMs = (entry: VideoToolboxActivityLogEntry): number => entry.kind === 'speech' ? entry.updatedAtMs : entry.capturedAtMs;

export const buildVoiceAppliedLogEntry = (
  summary: string,
  utterance: string,
  confidence: number,
  jevDetail: string,
  entryId: string,
  capturedAtMs = Date.now(),
): VideoToolboxVoiceAppliedLogEntry => ({
  kind: 'voice-applied',
  id: entryId,
  capturedAtMs,
  summary,
  utterance,
  confidence,
  jevDetail,
});

export const buildVoiceProposedLogEntry = (
  proposalId: string,
  summary: string,
  utterance: string,
  confidence: number,
  jevDetail: string,
  capturedAtMs = Date.now(),
): VideoToolboxVoiceProposedLogEntry => ({
  kind: 'voice-proposed',
  id: proposalId,
  proposalId,
  capturedAtMs,
  summary,
  utterance,
  confidence,
  jevDetail,
});

export const buildVoiceRejectedLogEntry = (
  utterance: string,
  reason: string,
  entryId: string,
  capturedAtMs = Date.now(),
): VideoToolboxVoiceRejectedLogEntry => ({
  kind: 'voice-rejected',
  id: entryId,
  capturedAtMs,
  utterance,
  reason,
});

/** Newest wall-clock events first. */
export const sortActivityLogEntries = (
  entries: VideoToolboxActivityLogEntry[],
): VideoToolboxActivityLogEntry[] => [...entries].sort((left, right) => getActivityLogEntrySortMs(right) - getActivityLogEntrySortMs(left));
